import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://sakthihospitals.com';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/t64300ho_SH%20Logo.png';

export function SEOHead({ title, description, path = '/', type = 'website', keywords = '' }) {
  const fullUrl = `${SITE_URL}${path}`;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={LOGO_URL} />
      <meta property="og:site_name" content="Sakthi Hospital & Research Centre" />
      <meta property="og:locale" content="en_IN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
}

export function BreadcrumbSchema({ items }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.path}`
    }))
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function FAQSchema({ questions }) {
  if (!questions || questions.length === 0) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map(q => ({
      "@type": "Question",
      "name": q.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.a
      }
    }))
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function MedicalSpecialtySchema({ name, description, procedures = [], doctor = null }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "MedicalSpecialty",
    "name": name,
    "description": description,
    "relevantSpecialty": {
      "@type": "MedicalSpecialty",
      "name": name
    },
    "availableService": procedures.map(p => ({
      "@type": "MedicalProcedure",
      "name": p
    })),
    ...(doctor && {
      "member": {
        "@type": "Physician",
        "name": doctor.name,
        "description": doctor.qualifications,
        "memberOf": {
          "@type": "Hospital",
          "name": "Sakthi Hospital & Research Centre"
        }
      }
    })
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function SpeakableSchema({ headline, summary, path = '/' }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": headline,
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["[data-testid='hero-main-tagline']", "[data-testid='about-section'] h2", "[data-testid='specialities-section'] h2"]
    },
    "url": `${SITE_URL}${path}`
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function LocalBusinessSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Sakthi Hospital & Research Centre",
    "alternateName": ["Sakthi Hospital", "SHRC", "Sakthi Hospital Triplicane", "Best Hospital in Chennai", "Best Hospital in Triplicane", "Best Hospital in Royapettah"],
    "description": "Best hospital in Chennai - NABH Accredited 75-bedded multi-specialty hospital in Triplicane. Top-rated hospital trusted by 2,00,000+ families for 25+ years. Best Gynaecology, Cardiology, Urology & Orthopaedics hospital in Chennai. 24/7 Emergency Care. Affordable treatment. Serving Triplicane, Royapettah, Mylapore, Chepauk, Mandaveli.",
    "url": SITE_URL,
    "logo": LOGO_URL,
    "image": LOGO_URL,
    "telephone": "+91-44-2844-2626",
    "email": "info@sakthihospitals.com",
    "foundingDate": "1985",
    "numberOfEmployees": { "@type": "QuantitativeValue", "value": 200, "unitText": "APPROXIMATE" },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "175, Big Street, Police Quarters, Triplicane",
      "addressLocality": "Chennai",
      "addressRegion": "Tamil Nadu",
      "postalCode": "600005",
      "addressCountry": "IN"
    },
    "geo": { "@type": "GeoCoordinates", "latitude": 13.0570045, "longitude": 80.2757397 },
    "hasMap": "https://maps.google.com/?cid=7927865571244753681",
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"], "opens": "09:00", "closes": "21:00" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": "Sunday", "opens": "09:00", "closes": "13:00" }
    ],
    "department": [
      { "@type": "MedicalClinic", "name": "Gynaecology & Maternity", "medicalSpecialty": "Gynecology" },
      { "@type": "MedicalClinic", "name": "Cardiology", "medicalSpecialty": "Cardiovascular" },
      { "@type": "MedicalClinic", "name": "Urology", "medicalSpecialty": "Urologic" },
      { "@type": "MedicalClinic", "name": "Orthopaedics", "medicalSpecialty": "Musculoskeletal" },
      { "@type": "MedicalClinic", "name": "Gastroenterology", "medicalSpecialty": "Gastroenterologic" },
      { "@type": "MedicalClinic", "name": "General Medicine", "medicalSpecialty": "PrimaryCare" },
      { "@type": "MedicalClinic", "name": "ENT", "medicalSpecialty": "Otolaryngologic" },
      { "@type": "MedicalClinic", "name": "Paediatrics", "medicalSpecialty": "Pediatric" },
      { "@type": "MedicalClinic", "name": "Diabetology", "medicalSpecialty": "Endocrine" },
      { "@type": "MedicalClinic", "name": "Dermatology", "medicalSpecialty": "Dermatologic" },
      { "@type": "MedicalClinic", "name": "Spine & Neurology", "medicalSpecialty": "Neurologic" },
      { "@type": "MedicalClinic", "name": "Oncology", "medicalSpecialty": "Oncologic" },
      { "@type": "MedicalClinic", "name": "General Surgery", "medicalSpecialty": "Surgical" },
      { "@type": "MedicalClinic", "name": "Plastic Surgery", "medicalSpecialty": "PlasticSurgery" }
    ],
    "availableService": [
      { "@type": "MedicalProcedure", "name": "24/7 Emergency Care" },
      { "@type": "MedicalProcedure", "name": "ICU with Intensivist" },
      { "@type": "MedicalProcedure", "name": "NICU" },
      { "@type": "MedicalProcedure", "name": "Dialysis" },
      { "@type": "MedicalProcedure", "name": "Laparoscopic Surgery" },
      { "@type": "MedicalProcedure", "name": "Joint Replacement" },
      { "@type": "MedicalProcedure", "name": "Maternity & Normal Delivery" },
      { "@type": "MedicalProcedure", "name": "IVF & Fertility Treatment" },
      { "@type": "MedicalProcedure", "name": "Ambulance Service" }
    ],
    "hasCredential": [
      { "@type": "EducationalOccupationalCredential", "credentialCategory": "NABH Accreditation", "dateCreated": "2017" }
    ],
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.2", "bestRating": "5", "reviewCount": "800" },
    "sameAs": [
      "https://www.facebook.com/sakthihospitals",
      "https://www.instagram.com/sakthi_hospital/",
      "https://in.linkedin.com/company/sakthihospitals",
      "https://www.youtube.com/channel/UCLHTOwTj_J3MiSaSNXTDRWw"
    ],
    "areaServed": [
      { "@type": "City", "name": "Chennai" },
      { "@type": "Place", "name": "Triplicane" },
      { "@type": "Place", "name": "Royapettah" },
      { "@type": "Place", "name": "Mylapore" },
      { "@type": "Place", "name": "Chepauk" },
      { "@type": "Place", "name": "Mandaveli" },
      { "@type": "Place", "name": "Alwarpet" },
      { "@type": "Place", "name": "Nungambakkam" },
      { "@type": "Place", "name": "T. Nagar" },
      { "@type": "Place", "name": "Egmore" },
      { "@type": "Place", "name": "Anna Salai" },
      { "@type": "Place", "name": "Thousand Lights" },
      { "@type": "Place", "name": "Wallajah Road" },
      { "@type": "Place", "name": "Central Chennai" }
    ],
    "priceRange": "$$",
    "paymentAccepted": "Cash, Credit Card, Debit Card, UPI, Insurance",
    "currenciesAccepted": "INR"
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
